import { makeAutoObservable, runInAction } from 'mobx';
import { FriendRequest, FriendshipStatus } from '../models/FriendRequest';
import { agent } from '../api/agent';
import { store } from './store';

export default class FriendRequestStore {
  friendshipStatusMap = new Map<string, FriendshipStatus>();
  pendingRequests: FriendRequest[] = [];
  loading = false;
  loadingStatus = new Map<string, boolean>();

  constructor() {
    makeAutoObservable(this);
  }

  get pendingRequestsCount() {
    return this.pendingRequests.length;
  }

  loadFriendshipStatus = async (userId: string) => {
    this.setLoadingStatus(userId, true);
    try {
      const status = await agent.FriendRequests.getStatus(userId);
      runInAction(() => {
        this.friendshipStatusMap.set(userId, status);
      });
      return status;
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.setLoadingStatus(userId, false);
    }
  };

  getFriendshipStatus = (userId: string): FriendshipStatus | undefined => {
    return this.friendshipStatusMap.get(userId);
  };

  sendFriendRequest = async (userId: string) => {
    this.setLoadingStatus(userId, true);
    try {
      await agent.FriendRequests.send(userId);
      await this.loadFriendshipStatus(userId);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.setLoadingStatus(userId, false);
    }
  };

  cancelFriendRequest = async (userId: string, requestId: string) => {
    this.setLoadingStatus(userId, true);
    try {
      await agent.FriendRequests.cancel(requestId);
      await this.loadFriendshipStatus(userId);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.setLoadingStatus(userId, false);
    }
  };

  acceptFriendRequest = async (userId: string, requestId: string) => {
    this.setLoadingStatus(userId, true);
    try {
      await agent.FriendRequests.accept(requestId);
      await this.loadFriendshipStatus(userId);
      runInAction(() => {
        this.pendingRequests = this.pendingRequests.filter(r => r.id !== requestId);
      });
      // Update friendship in profile store
      //store.profileStore?.updateFriendship(userId, true);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.setLoadingStatus(userId, false);
    }
  };

  rejectFriendRequest = async (userId: string, requestId: string) => {
    this.setLoadingStatus(userId, true);
    try {
      await agent.FriendRequests.reject(requestId);
      await this.loadFriendshipStatus(userId);
      runInAction(() => {
        this.pendingRequests = this.pendingRequests.filter(r => r.id !== requestId);
      });
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.setLoadingStatus(userId, false);
    }
  };

  unfriend = async (userId: string) => {
    this.setLoadingStatus(userId, true);
    try {
      await agent.FriendRequests.unfriend(userId);
      await this.loadFriendshipStatus(userId);
      // Update friendship in profile store
      //store.profileStore?.updateFriendship(userId, false);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.setLoadingStatus(userId, false);
    }
  };

  loadPendingRequests = async () => {
    this.loading = true;
    try {
      const requests = await agent.FriendRequests.getPending();
      runInAction(() => {
        this.pendingRequests = requests;
      });
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  clearFriendshipStatus = (userId: string) => {
    this.friendshipStatusMap.delete(userId);
    this.loadingStatus.delete(userId);
  };

  clearAllStatuses = () => {
    this.friendshipStatusMap.clear();
    this.loadingStatus.clear();
  };

  private setLoadingStatus = (userId: string, loading: boolean) => {
    runInAction(() => {
      this.loadingStatus.set(userId, loading);
    });
  };

  isLoading = (userId: string): boolean => {
    return this.loadingStatus.get(userId) || false;
  };

  updateFriendshipInSearchResults = (userId: string, isFriend: boolean) => {
    // This method will be implemented in FriendStore
    // Just a placeholder here for reference
  };
}